<template>
   <div class="account-subsection mb-6 max-w-80ch border-b border-grey-20 pb-6" data-cy="account-subsection">
      <h3 class="text-bolder mb-6 inline-block" data-cy="account-subsection-heading">{{ subsection.heading }}</h3>
      <RichTextRenderer
         v-if="subsection.description"
         :document="subsection.description"
         data-cy="account-subsection-description"
      />
      <dl data-cy="account-subsection-items" class="account-subsection-items grid gap-6">
         <template v-for="item in visibleItems" :key="`account-page-subsection-${item.type}`">
            <component :is="accountSubsectionComponents[item.type]" :field="item" />
         </template>
      </dl>
   </div>
</template>
<script setup lang="ts">
import { useUserStore } from "@/stores/userStore";
import { type Subsection } from "./accountTypes";

const userStore = useUserStore();

// Dynamic subsection components
// -----------------------------------------
import AccountProfileAddress from "./profileFields/AccountProfileAddress.vue";
import AccountProfileDateOfBirth from "./profileFields/AccountProfileDateOfBirth.vue";
import AccountProfileBackupEmail from "./profileFields/AccountProfileBackupEmail.vue";
import AccountProfileFullName from "./profileFields/AccountProfileFullName.vue";
import AccountProfileContactPreferences from "./profileFields/AccountProfileContactPreferences.vue";
import AccountProfileGenderIdentity from "./profileFields/AccountProfileGenderIdentity.vue";
import AccountProfileLocalePreference from "./profileFields/AccountProfileLocalePreference.vue";
import AccountProfileMarketingPreferences from "./profileFields/AccountProfileMarketingPreferences.vue";
import AccountProfilePronouns from "./profileFields/AccountProfilePronouns.vue";
import AccountProfilePreferredName from "./profileFields/AccountProfilePreferredName.vue";
import AccountProfileSecondaryContactNumber from "./profileFields/AccountProfileSecondaryPhoneNumber.vue";
import AccountProfileSex from "./profileFields/AccountProfileSex.vue";
import AccountProfilePrimaryContactNumber from "./profileFields/AccountProfilePrimaryContactNumber.vue";
import AccountProfilePrimaryEmail from "./profileFields/AccountProfilePrimaryEmail.vue";
import DeleteAccount from "./DeleteAccount.vue";
import PasswordReset from "./PasswordReset.vue";

interface AccountSubsectionComponents {
   [key: string]: Component;
}

const accountSubsectionComponents: AccountSubsectionComponents = {
   reset_password_button: PasswordReset,
   account_delete_button: DeleteAccount,
   profile_address: AccountProfileAddress,
   profile_backup_email: AccountProfileBackupEmail,
   profile_contact_consent: AccountProfileMarketingPreferences,
   profile_date_of_birth: AccountProfileDateOfBirth,
   profile_email: AccountProfilePrimaryEmail,
   profile_full_name: AccountProfileFullName,
   profile_gender: AccountProfileGenderIdentity,
   profile_pronouns: AccountProfilePronouns,
   profile_phone_number: AccountProfilePrimaryContactNumber,
   profile_preferred_contact_methods: AccountProfileContactPreferences,
   profile_preferred_locale: AccountProfileLocalePreference,
   profile_preferred_name: AccountProfilePreferredName,
   profile_secondary_phone_number: AccountProfileSecondaryContactNumber,
   profile_sex: AccountProfileSex,
};

// Props
// ------------------------------------
const props = defineProps<{
   subsection: Subsection;
}>();

const visibleItems = computed(() => {
   return props.subsection.items.filter((item) => {
      const componentExists = !!accountSubsectionComponents[item.type];
      const isPasswordReset = item.type === "reset_password_button";

      // Only show the password reset button if the user is not social login user
      if (isPasswordReset) {
         return componentExists && !userStore.user.isSocialLogin;
      }

      return componentExists;
   });
});
</script>

<style lang="scss" scoped>
// Hide the whole subsection if no items are present
// This might happen when user has no data to display (for now we don't show empty fields)
// This will change in milestone 2, when edit functionality is added
.account-subsection:has(.account-subsection-items:empty) {
   display: none;
}
</style>
